import { useState } from 'react'
import { useDeclararCredito } from '../../../api/queries/creditoDaIa'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'

interface Props {
  competencia: string
  aoFechar: () => void
}

/** O piso do motivo, igual ao do servidor e ao CHECK da V40. */
const MINIMO_DO_MOTIVO = 10

/**
 * Declarar o crédito do mês.
 *
 * **Não existe "editar".** O crédito é o número que responde, depois, por que a análise parou num
 * determinado dia — e um `UPDATE` por cima apaga a linha que responde. Para trocar, revoga-se com
 * motivo e declara-se outro; os dois ficam lado a lado.
 *
 * @param props o mês e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeCredito({ competencia, aoFechar }: Props) {
  const t = TEXTOS.creditoDaIa.credito
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const declarar = useDeclararCredito()

  const motivoCurto = motivo.trim().length < MINIMO_DO_MOTIVO
  const valorInvalido = !valor || Number(valor.replace(',', '.')) <= 0

  const confirmar = () => {
    declarar.mutate(
      { competencia, valor: valor.replace(',', '.'), motivo: motivo.trim() },
      { onSuccess: aoFechar },
    )
  }

  return (
    <Dialogo
      titulo={t.titulo}
      descricao={t.valorAjuda}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao onClick={confirmar} disabled={motivoCurto || valorInvalido || declarar.isPending}>
            {t.confirmar}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <CampoDeTexto
          rotulo={`${t.valor} (${competencia})`}
          type="number"
          min="0.01"
          step="0.01"
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
        <CampoDeTextoLongo
          rotulo={t.motivo}
          ajuda={t.motivoAjuda}
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          erro={motivo.length > 0 && motivoCurto ? TEXTOS.motivoObrigatorio : undefined}
        />
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">{t.aviso}</p>
        {declarar.isError && <AvisoDeErro erro={declarar.error} />}
      </div>
    </Dialogo>
  )
}

/**
 * Revogar um crédito — com motivo, sempre.
 *
 * @param props o crédito e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeRevogacao({
  creditoId,
  aoFechar,
  revogar,
}: {
  creditoId: number
  aoFechar: () => void
  revogar: ReturnType<typeof import('../../../api/queries/creditoDaIa').useRevogarCredito>
}) {
  const t = TEXTOS.creditoDaIa.credito
  const [motivo, setMotivo] = useState('')
  const curto = motivo.trim().length < MINIMO_DO_MOTIVO

  return (
    <Dialogo
      titulo={t.revogar}
      descricao={t.aviso}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            aparencia="perigo"
            disabled={curto || revogar.isPending}
            onClick={() => revogar.mutate({ id: creditoId, motivo: motivo.trim() }, { onSuccess: aoFechar })}
          >
            {t.revogar}
          </Botao>
        </>
      }
    >
      <CampoDeTextoLongo
        rotulo={t.revogarMotivo}
        ajuda={t.motivoAjuda}
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        erro={motivo.length > 0 && curto ? TEXTOS.motivoObrigatorio : undefined}
      />
      {revogar.isError && <AvisoDeErro erro={revogar.error} />}
    </Dialogo>
  )
}
